import T from "@babackapp/translations/t-component";
import type { Translation } from "@babackapp/translations/use-translation";
import { toast } from "@babackapp/ui/components/sonner";
import {
	MutationCache,
	QueryClientProvider as NativeProvider,
	QueryClient,
	type QueryKey,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { CheckCircleIcon } from "lucide-react";
import type { PropsWithChildren } from "react";

declare module "@tanstack/react-query" {
	interface Register {
		mutationMeta: {
      invalidatesQuery?: QueryKey | QueryKey[];
			success?: {
				title?: string;
				message?: string;
			};
			error?: {
				title?: string;
				message?: string;
			};
		};
	}
}

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 20,
			refetchOnMount: false,
		},
	},
	mutationCache: new MutationCache({
		onSuccess: (_data, _variables, _context, mutation) => {
			if (mutation.meta?.success) {
				toast(
					<div className="flex gap-2 items-center">
						<div className="bg-muted text-muted-foreground size-8 rounded-full flex items-center justify-center">
							<CheckCircleIcon className="size-5" />
						</div>
						<div className="flex flex-col flex-1">
							{mutation.meta.success.title && (
								<span className="text-sm font-medium">
									<T keyName={mutation.meta.success.title as Translation} />
								</span>
							)}
							{mutation.meta.success.message && (
								<span className="text-xs text-muted-foreground">
									<T keyName={mutation.meta.success.message as Translation} />
								</span>
							)}
						</div>
					</div>,
				);
			}
		},
		onError: (_error, _variables, _context, mutation) => {
			if (mutation.meta?.error) {
				toast(
					<div className="flex gap-2 items-center">
						<div className="bg-destructive/20 text-destructive border-destructive size-8 rounded-full flex items-center justify-center">
							<CheckCircleIcon className="size-5" />
						</div>
						<div className="flex flex-col flex-1">
							{mutation.meta.error.title && (
								<span className="text-sm font-medium">
									<T keyName={mutation.meta.error.title as Translation} />
								</span>
							)}
							{mutation.meta.error.message && (
								<span className="text-xs text-muted-foreground">
									<T keyName={mutation.meta.error.message as Translation} />
								</span>
							)}
						</div>
					</div>,
				);
			}
		},
		onSettled: (_data, _error, _variables, _context, mutation) => {
			if (mutation.meta?.invalidatesQuery) {
        const queries = Array.isArray(mutation.meta.invalidatesQuery[0])
          ? (mutation.meta.invalidatesQuery as QueryKey[])
          : [mutation.meta.invalidatesQuery as QueryKey];

        for (const queryKey of queries) {
          queryClient.invalidateQueries({ queryKey });
        }
			}
		},
	}),
});

export default function QueryClientProvider({ children }: PropsWithChildren) {
	return (
		<NativeProvider client={queryClient}>
			{children}
			<ReactQueryDevtools />
		</NativeProvider>
	);
}
